// @flow
import Spinner from 'components/Spinner'
import { useFilters } from 'hooks/useQuery'
import useScreenSizeStyle from 'hooks/useScreenSizeStyle'
import { useAddExportFiles } from 'queries/export_files/useAddExportFiles'
import { useDownloadExportFiles } from 'queries/export_files/useDownloadExportFiles'
import { useLoadExportFiles } from 'queries/export_files/useLoadExportFiles'
import { useLoadExport } from 'queries/exports/useLoadExports'
import * as React from 'react'
import { Helmet } from 'react-helmet'
import { useParams } from 'react-router-dom'
import Badge from 'ui/atoms/Badge'
import Button from 'ui/atoms/Button'
import Flex from 'ui/atoms/Flex'
import Pagination from 'ui/atoms/Pagination'
import Table from 'ui/atoms/Table'
import Text from 'ui/atoms/Text'
import Icons from 'ui/Icons'
import Page from 'ui/organisms/Page'
import { getFormattedDate } from 'utils/dates'

function ExportsDetail(): React.Node {
  const { formId } = useParams()
  const { filters } = useFilters({ defaultValues: { page: 1, pageSize: 25 } })

  const {
    data: exportData,
    isLoading: isExportLoading,
    isFetching: isExportFetching,
  } = useLoadExport({ id: formId })
  const {
    data: filesData,
    isLoading: isFileDataLoading,
    isFetching: isFileDataFetching,
    refetch,
  } = useLoadExportFiles(exportData?.id, filters, {
    enabled: !!exportData,
    keepPreviousData: true,
  })
  const { addExportFile, isLoading: isAddExportFileLoading } =
    useAddExportFiles(exportData?.id)
  const { downloadExportFile, isLoading: isDownloadExportFileLoading } =
    useDownloadExportFiles({})

  const isProcessing = filesData?.results.some((item) =>
    ['PEDING', 'PROCESSING'].includes(item.status)
  )

  React.useEffect(() => {
    if (isProcessing) {
      const interval = setInterval(() => {
        refetch()
      }, 5000) // 5 seconds

      return () => {
        clearInterval(interval)
      }
    }
  }, [isProcessing, refetch])

  const isLoading =
    isExportLoading ||
    isExportFetching ||
    isFileDataLoading ||
    isFileDataFetching

  const textSize = useScreenSizeStyle({
    lg: { small: true, xsmall: false },
    generic: { small: false, xsmall: true },
  })
  const maxWidthText = useScreenSizeStyle({
    md: '170px',
    generic: '110px',
  })

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'DONE':
        return <Badge $success text="Ready" />
      case 'ERROR':
        return <Badge $error text="Error" />
      default:
        return <Badge $warning text="Processing" />
    }
  }

  return (
    <>
      <Helmet>
        <title>Exports/Zentake</title>
      </Helmet>

      <Page withBackButton title="Exports">
        <Table.Title className="justify-start">
          <Flex
            align="center"
            className="text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider w-full bg-gray-50"
            justify="between"
          >
            <Text $light $medium $xsmall>
              FORM NAME: {exportData?.form.name || 'N/A'}
            </Text>
            <Button
              disabled={isProcessing || isAddExportFileLoading}
              onClick={async () => {
                await addExportFile({ export: exportData.id })
              }}
            >
              {isAddExportFileLoading && isProcessing && (
                <Spinner color="black" style={{ display: 'inline-block' }} />
              )}
              Generate Export CSV
            </Button>
          </Flex>
        </Table.Title>
        <Table>
          <Table.Head
            columns={['DATE CREATED', 'FORM COUNT', 'STATUS', 'DOWNLOAD']}
          />

          <Table.Body>
            {isLoading && (
              <Table.Row>
                <Table.Column>
                  <Text>Loading...</Text>
                </Table.Column>
                <Table.Column />
                <Table.Column />
                <Table.Column />
              </Table.Row>
            )}

            {!isLoading && filesData?.results.length === 0 && (
              <Table.Row>
                <Table.Column>
                  <Text>No Results Found</Text>
                </Table.Column>
                <Table.Column />
                <Table.Column />
                <Table.Column />
              </Table.Row>
            )}

            {!isLoading &&
              filesData?.results.map((item) => (
                <Table.Row key={item.id}>
                  <Table.Column>
                    <Text
                      $truncate
                      $small={textSize.small}
                      $xsmall={textSize.xsmall}
                      style={{ maxWidth: maxWidthText }}
                    >
                      {getFormattedDate(
                        item.created_at,
                        'MM/DD/YYYY, h:mm:ss a'
                      )}
                    </Text>
                  </Table.Column>
                  <Table.Column>
                    <Text
                      $small={textSize.small}
                      $xsmall={textSize.xsmall}
                      style={{ maxWidth: maxWidthText }}
                    >
                      {item.form_count}
                    </Text>
                  </Table.Column>
                  <Table.Column>{getStatusBadge(item.status)}</Table.Column>
                  <Table.Column>
                    <Button
                      $plain
                      disabled={
                        isDownloadExportFileLoading || item.status !== 'DONE'
                      }
                      onClick={async () => {
                        await downloadExportFile({ id: item.id })
                      }}
                    >
                      {(isDownloadExportFileLoading ||
                        item.status !== 'DONE') && (
                        <Spinner
                          color="black"
                          style={{ display: 'inline-block' }}
                        />
                      )}
                      <Icons.DocumentDownload className="ml-0" />
                      Download CSV
                    </Button>
                  </Table.Column>
                </Table.Row>
              ))}
          </Table.Body>
        </Table>
        <Pagination.Border>
          <Pagination total={filesData?.count || 0} />
        </Pagination.Border>
      </Page>
    </>
  )
}

export default ExportsDetail
